const STORAGE_KEY = 'mielleglow-scratch-popup';
const ARRIVAL_KEY = 'mielleglow-scratch-arrival';
const DAY = 24 * 60 * 60 * 1000;
const CONFETTI_COLORS = ['#ffd166', '#d9a93a', '#1d4f86', '#7cc4f0', '#2e9e5b', '#08243b'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// "mielleglow.shop." (trailing dot) is a separate site for the browser: its own storage and
// cookies, so the pop-up and the cart would forget the visitor. Send it to the normal address.
if (location.hostname.endsWith('.')) {
  const url = new URL(location.href);
  url.hostname = url.hostname.replace(/\.+$/, '');
  location.replace(url.href);
}

/** Storage can throw in private windows: the pop-up must keep working without it */
const storage = {
  get(store, key) {
    try {
      return store.getItem(key);
    } catch {
      return null;
    }
  },
  set(store, key, value) {
    try {
      store.setItem(key, value);
    } catch {}
  },
};

class ScratchPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.card = this.querySelector('[data-card]');
    this.foil = this.querySelector('[data-foil]');
    this.form = this.querySelector('form');
    this.email = this.querySelector('[data-email]');
    this.error = this.querySelector('[data-error]');
    this.teaser = this.querySelector('[data-teaser]');
    this.code = this.dataset.code;
    this.designMode = this.hasAttribute('data-design-mode');
    this.revealed = false;
    this.scratched = false;

    this.form.noValidate = true;
    this.form.addEventListener('submit', this.onSubmit);
    this.foil.addEventListener('pointerdown', this.onPointerDown);
    this.foil.addEventListener('pointermove', this.onPointerMove);
    this.foil.addEventListener('pointerup', this.onPointerUp);
    this.foil.addEventListener('pointercancel', this.onPointerUp);
    this.dialog.addEventListener('close', this.onClose);
    this.dialog.addEventListener('pointerdown', (event) => (this.pointerDownTarget = event.target));
    this.dialog.addEventListener('click', this.onDialogClick);
    this.email.addEventListener('input', () => this.setError(false));
    document.addEventListener('keydown', this.onKeyDown);
    this.querySelector('[data-reveal]').addEventListener('click', () => this.reveal());
    this.card.addEventListener('click', () => {
      if (this.card.classList.contains('is-locked')) this.email.focus();
    });
    this.querySelector('[data-copy]')?.addEventListener('click', this.copyCode);
    this.querySelector('[data-shop]').addEventListener('click', () => this.save({ state: 'subscribed' }));
    this.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.teaser?.querySelector('[data-teaser-open]').addEventListener('click', () => this.open());
    this.teaser?.querySelector('[data-teaser-close]').addEventListener('click', () => {
      this.teaser.setAttribute('hidden', '');
      this.save({ teaserHidden: true });
    });
    window.addEventListener('resize', this.onResize);

    if (this.designMode) {
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      return;
    }

    this.schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    this.unlockScroll();
  }

  get data() {
    try {
      return JSON.parse(storage.get(localStorage, STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  }

  save(changes) {
    if (this.designMode) return;
    storage.set(localStorage, STORAGE_KEY, JSON.stringify({ ...this.data, ...changes }));
  }

  schedule() {
    const { state, closedAt } = this.data;
    if (state === 'subscribed') return;

    if (closedAt && Date.now() - closedAt < Number(this.dataset.daysHidden) * DAY) {
      this.showTeaser();
      return;
    }

    // The delay counts from the first page of the visit, not from each page
    let arrival = Number(storage.get(sessionStorage, ARRIVAL_KEY));
    if (!arrival) {
      arrival = Math.round(performance.timeOrigin) || Date.now();
      storage.set(sessionStorage, ARRIVAL_KEY, String(arrival));
    }
    const wait = Math.max(0, Number(this.dataset.delay) * 1000 - (Date.now() - arrival));
    this.timer = setTimeout(this.tryOpen, wait);
  }

  // Wait while the cookie banner, the cart drawer or another dialog is open, or the tab is hidden
  tryOpen = () => {
    const cookieBanner = document.getElementById('shopify-pc__banner');
    if (document.hidden || document.querySelector('dialog[open]') || cookieBanner?.checkVisibility?.()) {
      this.timer = setTimeout(this.tryOpen, 2500);
      return;
    }
    this.open();
  };

  open(step) {
    if (this.dialog.open) return;
    step ??= 'email';

    this.openedAt = Date.now();
    this.teaser?.setAttribute('hidden', '');
    this.lockScroll();
    // Not a modal: Shopify's captcha challenge must be able to show above the pop-up
    this.dialog.show();
    this.querySelector('[data-panel]').focus({ preventScroll: true });
    this.showStep(step);
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  onClose = () => {
    this.unlockScroll();
    if (this.designMode || this.data.state === 'subscribed') return;
    this.save({ closedAt: Date.now() });
    this.showTeaser();
  };

  onKeyDown = (event) => {
    if (event.key === 'Escape' && this.dialog.open) this.close();
  };

  // Close on a click outside the panel, but not at the end of a scratch that left the card
  onDialogClick = (event) => {
    if (event.target === this.dialog && this.pointerDownTarget === this.dialog) this.close();
  };

  onSectionSelect = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;
    this.revealed = false;
    this.scratched = false;
    this.card.classList.remove('is-revealed');
    this.open('email');
  };

  onSectionDeselect = (event) => {
    if (event.detail.sectionId === this.dataset.sectionId) this.close();
  };

  showTeaser() {
    if (this.data.teaserHidden) return;
    this.teaser?.removeAttribute('hidden');
  }

  lockScroll() {
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    document.documentElement.style.removeProperty('overflow');
  }

  showStep(step) {
    for (const element of this.querySelectorAll('[data-step]')) {
      element.toggleAttribute('hidden', element.dataset.step !== step);
    }
    this.querySelector('.scratch-popup__inner').scrollTop = 0;

    // One card: locked preview in the email step, scratchable in the next step
    const slot = this.querySelector(`[data-step="${step}"] [data-card-slot]`);
    if (slot) {
      slot.append(this.card);
      this.card.classList.toggle('is-locked', step === 'email');
      // Wait for layout and the heading font so the card text is drawn right
      if (!this.scratched) requestAnimationFrame(() => document.fonts.ready.then(() => this.drawFoil()));
    }
    // A keyboard on phones would hide the offer, so only focus the field with a mouse
    if (step === 'email' && matchMedia('(pointer: fine)').matches) {
      this.email.focus({ preventScroll: true });
    }
  }

  /* Scratch card */

  drawFoil() {
    const { width, height } = this.card.getBoundingClientRect();
    if (!width || !height) return;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const canvas = this.foil;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);

    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.globalCompositeOperation = 'source-over';

    const gold = context.createLinearGradient(0, 0, width, height);
    gold.addColorStop(0, '#fbecb0');
    gold.addColorStop(0.3, '#ecca6b');
    gold.addColorStop(0.5, '#f8e19a');
    gold.addColorStop(0.75, '#d9a93a');
    gold.addColorStop(1, '#f1d27c');
    context.fillStyle = gold;
    context.fillRect(0, 0, width, height);

    // Brushed metal lines
    context.strokeStyle = 'rgb(255 255 255 / 0.16)';
    context.lineWidth = 1;
    for (let x = -height; x < width; x += 9) {
      context.beginPath();
      context.moveTo(x, height);
      context.lineTo(x + height * 0.6, 0);
      context.stroke();
    }

    // Inner frame
    context.strokeStyle = 'rgb(255 255 255 / 0.65)';
    context.lineWidth = 1.5;
    context.beginPath();
    context.roundRect(10, 10, width - 20, height - 20, 10);
    context.stroke();

    // Shine strokes in the corner
    context.strokeStyle = 'rgb(255 255 255 / 0.85)';
    context.lineWidth = 2.5;
    context.lineCap = 'round';
    for (let i = 0; i < 3; i++) {
      context.beginPath();
      context.moveTo(width - 78 + i * 4, 30 + i * 7);
      context.quadraticCurveTo(width - 54, 22 + i * 7, width - 30 - i * 4, 26 + i * 7);
      context.stroke();
    }

    const styles = getComputedStyle(this.card);
    const font = styles.getPropertyValue('--font-heading--family').trim() || styles.fontFamily;
    context.fillStyle = getComputedStyle(this).getPropertyValue('--scratch-ink').trim() || '#08243b';
    context.font = `700 ${Math.round(width * 0.095)}px ${font}`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(this.dataset.cardText || 'Scratch here!', width / 2, height / 2);

    this.context = context;
    this.brush = Math.max(20, width * 0.085);
  }

  point(event) {
    const rect = this.foil.getBoundingClientRect();
    // Map to the size the card was drawn at, even after a resize
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    return {
      x: ((event.clientX - rect.left) / rect.width) * (this.foil.width / ratio),
      y: ((event.clientY - rect.top) / rect.height) * (this.foil.height / ratio),
    };
  }

  scratch(from, to) {
    const context = this.context;
    context.globalCompositeOperation = 'destination-out';
    context.lineWidth = this.brush * 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.beginPath();
    context.moveTo(from.x, from.y);
    context.lineTo(to.x, to.y);
    context.stroke();
  }

  onPointerDown = (event) => {
    if (this.revealed || !this.context || this.card.classList.contains('is-locked')) return;
    event.preventDefault();
    this.scratching = true;
    this.scratched = true;
    this.moves = 0;
    this.foil.setPointerCapture(event.pointerId);
    this.lastPoint = this.point(event);
    this.scratch(this.lastPoint, this.lastPoint);
  };

  onPointerMove = (event) => {
    if (!this.scratching) return;
    event.preventDefault();
    const point = this.point(event);
    this.scratch(this.lastPoint, point);
    this.lastPoint = point;
    if (++this.moves % 6 === 0) this.checkProgress();
  };

  onPointerUp = () => {
    if (!this.scratching) return;
    this.scratching = false;
    this.checkProgress();
  };

  checkProgress() {
    const { data } = this.context.getImageData(0, 0, this.foil.width, this.foil.height);
    let cleared = 0;
    let total = 0;
    // Sample one pixel in 24 (alpha channel only)
    for (let i = 3; i < data.length; i += 4 * 24) {
      total++;
      if (data[i] < 128) cleared++;
    }
    if (cleared / total > 0.45) this.reveal();
  }

  onResize = () => {
    if (this.dialog.open && !this.revealed && !this.scratched) this.drawFoil();
  };

  reveal() {
    if (this.revealed) return;
    this.revealed = true;
    this.scratching = false;
    this.card.classList.add('is-revealed');
    this.confetti();
    setTimeout(() => this.showStep('success'), 1500);
  }

  confetti() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const layer = this.querySelector('[data-confetti]');
    const random = (min, max) => min + Math.random() * (max - min);

    for (let i = 0; i < 70; i++) {
      const piece = document.createElement('i');
      piece.style.left = `${random(0, 100)}%`;
      piece.style.background = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      piece.style.setProperty('--drift', `${random(-90, 90)}px`);
      piece.style.setProperty('--spin', `${random(-720, 720)}deg`);
      piece.style.setProperty('--delay', `${random(0, 0.35)}s`);
      piece.style.setProperty('--duration', `${random(1.8, 3)}s`);
      if (i % 3 === 0) piece.style.borderRadius = '50%';
      layer.append(piece);
    }
    setTimeout(() => layer.replaceChildren(), 3600);
  }

  /* Sign-up */

  setError(visible, message = this.error.dataset.invalid) {
    if (visible) this.error.textContent = message;
    this.error.toggleAttribute('hidden', !visible);
    this.email.setAttribute('aria-invalid', String(visible));
  }

  /**
   * Sign-up sent in the background, so the visitor stays on the page and sees no captcha.
   * It goes to the MielleGlow sign-up relay (Cloudflare Worker), which adds the contact to
   * Omnisend as an email subscriber, with the pop-up tags.
   * Bots are filtered here: a hidden field people never fill, and a minimum time in the pop-up.
   */
  onSubmit = async (event) => {
    // Shopify's captcha listener must not take over the form
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.submitting) return;

    const email = this.email.value.trim();
    if (!EMAIL_PATTERN.test(email)) {
      this.setError(true);
      this.email.focus();
      return;
    }
    this.email.value = email;

    if (this.designMode) {
      this.onSubscribed();
      return;
    }

    // Bot: pretend it worked, send nothing
    if (this.querySelector('[data-honeypot]')?.value || Date.now() - this.openedAt < 1500) {
      this.showStep('scratch');
      return;
    }

    this.submitting = true;
    const button = this.form.querySelector('[type="submit"]');
    button.setAttribute('aria-busy', 'true');

    try {
      const tags = this.form.querySelector('[name="contact[tags]"]').value.split(',');
      // Plain text body: a "simple" request, so the browser sends no CORS preflight
      const response = await fetch(this.dataset.signupUrl, {
        method: 'POST',
        body: JSON.stringify({ email, tags }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) throw new Error(`Sign-up failed (${response.status} ${result.error || ''})`);
    } catch (error) {
      console.error(error);
      this.submitting = false;
      button.removeAttribute('aria-busy');
      this.setError(true, this.error.dataset.failed);
      return;
    }

    try {
      window.omnisend?.identifyContact?.({ email });
    } catch {}
    this.submitting = false;
    button.removeAttribute('aria-busy');
    this.onSubscribed();
  };

  onSubscribed() {
    this.save({ state: 'subscribed', teaserHidden: true });
    this.teaser?.setAttribute('hidden', '');
    // Apply the code to the cart so it is already there at checkout
    if (this.code && !this.designMode) {
      fetch(`/discount/${encodeURIComponent(this.code)}`, { credentials: 'same-origin' }).catch(() => {});
    }
    this.showStep('scratch');
  }

  copyCode = async () => {
    const label = this.querySelector('[data-copy-label]');
    try {
      await navigator.clipboard.writeText(this.code);
    } catch {
      return;
    }
    const text = label.textContent;
    label.textContent = label.dataset.copied;
    setTimeout(() => (label.textContent = text), 2000);
  };
}

if (!customElements.get('scratch-popup')) {
  customElements.define('scratch-popup', ScratchPopup);
}
